import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Gift, Palette, Scissors, Heart } from 'lucide-react';

export default function ArtikelDIYGeschenke() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]">
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />Alle Ratgeber
          </Link>
          <div className="flex items-center gap-2 text-[#D4C5A9] text-sm mb-4">
            <Calendar className="w-4 h-4" /><span>Februar 2026</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight">
            DIY Eid Geschenke: Kreativ & einfach selbstgemacht
          </h1>
          <p className="text-lg text-white/80 leading-relaxed">
            Selbstgemachte Geschenke von Herzen – mit Anleitungen für Anfänger und Fortgeschrittene.
          </p>
        </div>
      </section>

      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 prose prose-lg max-w-none">
          <p className="text-xl text-[#2C332E] leading-relaxed mb-8">
            Selbstgemachte Geschenke haben einen besonderen Wert – sie zeigen Zeit, Mühe und persönliche 
            Zuwendung. Zu Eid sind DIY-Geschenke besonders beliebt, weil sie die Wärme und Liebe des 
            Festes widerspiegeln. Hier findest du 15 Ideen mit einfachen Anleitungen.
          </p>

          <div className="bg-[#F5F5F0] rounded-2xl p-6 md:p-8 mb-12">
            <h2 className="font-serif text-xl font-medium text-[#2C332E] mb-4">📖 Kategorien</h2>
            <ul className="space-y-2 text-[#4A5D4F]">
              <li><a href="#kulinarisch" className="hover:underline">1. Kulinarische DIY-Geschenke</a></li>
              <li><a href="#deko" className="hover:underline">2. Dekoration & Karten</a></li>
              <li><a href="#pflege" className="hover:underline">3. Pflege & Wellness</a></li>
              <li><a href="#kinder" className="hover:underline">4. DIY mit Kindern</a></li>
            </ul>
          </div>

          <section id="kulinarisch" className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                <Heart className="w-6 h-6 text-[#4A5D4F]" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">1. Kulinarische DIY-Geschenke</h2>
            </div>

            <div className="space-y-6 mb-6">
              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🍫 Dattel-Trüffel (20 Min.)</h4>
                <p className="text-sm text-[#4B5563] mb-3">Einfach und lecker – perfekt als Eid-Mitbringsel!</p>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm font-medium text-[#2C332E] mb-2">Zutaten:</p>
                  <ul className="text-sm text-[#4B5563] mb-3 space-y-1">
                    <li>• 200g entkernte Datteln</li>
                    <li>• 100g gemahlene Mandeln</li>
                    <li>• 2 EL Kakao</li>
                    <li>• Kokosraspeln zum Wälzen</li>
                  </ul>
                  <p className="text-sm font-medium text-[#2C332E] mb-2">Anleitung:</p>
                  <ol className="text-sm text-[#4B5563] space-y-1">
                    <li>1. Datteln im Mixer zerkleinern</li>
                    <li>2. Mandeln und Kakao untermischen</li>
                    <li>3. Kleine Kugeln formen</li>
                    <li>4. In Kokosraspeln wälzen</li>
                    <li>5. 1 Stunde kalt stellen</li>
                  </ol>
                </div>
              </div>

              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🫖 Chai-Gewürzmischung (10 Min.)</h4>
                <p className="text-sm text-[#4B5563] mb-3">Orientalischer Tee zum Selbermachen.</p>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm font-medium text-[#2C332E] mb-2">Zutaten:</p>
                  <ul className="text-sm text-[#4B5563] mb-3 space-y-1">
                    <li>• 50g schwarzer Tee (lose)</li>
                    <li>• 1 TL Kardamom (gemahlen)</li>
                    <li>• 1 TL Zimt</li>
                    <li>• ½ TL Ingwer (gemahlen)</li>
                    <li>• ¼ TL Nelken (gemahlen)</li>
                  </ul>
                  <p className="text-sm font-medium text-[#2C332E] mb-2">Anleitung:</p>
                  <p className="text-sm text-[#4B5563]">Alle Zutaten mischen, in ein hübsches Glas füllen und mit einem Etikett versehen.</p>
                </div>
              </div>

              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🍪 Ma'amoul (Grießkekse)</h4>
                <p className="text-sm text-[#4B5563] mb-3">Traditionelle gefüllte Kekse – ein Klassiker zu Eid!</p>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm font-medium text-[#2C332E] mb-2">Zutaten:</p>
                  <ul className="text-sm text-[#4B5563] mb-3 space-y-1">
                    <li>• 500g Grieß (fein)</li>
                    <li>• 200g Butter</li>
                    <li>• 100g Puderzucker</li>
                    <li>• Dattel- oder Walnussfüllung</li>
                    <li>• 1 TL Rosenwasser</li>
                  </ul>
                  <p className="text-sm text-[#4B5563]">Tipp: Es gibt spezielle Ma'amoul-Formen, die wunderschöne Muster zaubern!</p>
                </div>
              </div>

              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🥜 Energy Balls (15 Min.)</h4>
                <p className="text-sm text-[#4B5563]">Gesunde Snacks aus Datteln, Nüssen und Haferflocken. Ideal als Iftar-Snack oder Eid-Geschenk.</p>
              </div>
            </div>
          </section>

          <section id="deko" className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                <Scissors className="w-6 h-6 text-[#4A5D4F]" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">2. Dekoration & Karten</h2>
            </div>

            <div className="space-y-6 mb-6">
              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🌙 Eid-Karten basteln</h4>
                <p className="text-sm text-[#4B5563] mb-3">Handgemachte Karten mit persönlicher Note.</p>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm font-medium text-[#2C332E] mb-2">Du brauchst:</p>
                  <ul className="text-sm text-[#4B5563] mb-3 space-y-1">
                    <li>• Blanko-Karten oder Tonpapier</li>
                    <li>• Goldfolie oder Glitzerstifte</li>
                    <li>• Mond- und Stern-Schablonen</li>
                    <li>• Aufkleber und Washi-Tape</li>
                  </ul>
                  <p className="text-sm text-[#4B5563]">Idee: Schreibe einen persönlichen Dua oder Segenswunsch in die Karte!</p>
                </div>
              </div>

              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🏮 Papierlaterne</h4>
                <p className="text-sm text-[#4B5563] mb-3">Einfache Laterne aus Papier mit LED-Teelicht.</p>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm font-medium text-[#2C332E] mb-2">Anleitung:</p>
                  <ol className="text-sm text-[#4B5563] space-y-1">
                    <li>1. Buntes Papier in der Mitte falten</li>
                    <li>2. Parallele Schnitte von der Falz zur Kante</li>
                    <li>3. Aufklappen und zu einem Zylinder rollen</li>
                    <li>4. Enden zusammenkleben</li>
                    <li>5. LED-Teelicht hineinstellen</li>
                  </ol>
                </div>
              </div>

              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">✨ Eid-Girlande</h4>
                <p className="text-sm text-[#4B5563]">Monde und Sterne aus Goldpapier an einer Schnur – festliche Dekoration für jeden Raum!</p>
              </div>

              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">📦 Geschenkbox dekorieren</h4>
                <p className="text-sm text-[#4B5563]">Einfache Box mit Kalligraphie, Bändern und "Eid Mubarak"-Aufkleber verschönern.</p>
              </div>
            </div>
          </section>

          <section id="pflege" className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                <Palette className="w-6 h-6 text-[#4A5D4F]" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">3. Pflege & Wellness</h2>
            </div>

            <div className="space-y-6 mb-6">
              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🧴 Körperöl mit Rosenduft</h4>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm font-medium text-[#2C332E] mb-2">Zutaten:</p>
                  <ul className="text-sm text-[#4B5563] mb-3 space-y-1">
                    <li>• 100ml Mandelöl oder Jojobaöl</li>
                    <li>• 10-15 Tropfen Rosenöl (ätherisch)</li>
                    <li>• Getrocknete Rosenblüten (optional)</li>
                  </ul>
                  <p className="text-sm text-[#4B5563]">Mischen, in eine hübsche Flasche füllen, fertig!</p>
                </div>
              </div>

              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🛁 Badebomben</h4>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm font-medium text-[#2C332E] mb-2">Zutaten:</p>
                  <ul className="text-sm text-[#4B5563] mb-3 space-y-1">
                    <li>• 200g Natron</li>
                    <li>• 100g Zitronensäure</li>
                    <li>• 50g Kokosöl</li>
                    <li>• Ätherische Öle nach Wahl</li>
                    <li>• Lebensmittelfarbe (optional)</li>
                  </ul>
                  <p className="text-sm text-[#4B5563]">In Formen pressen und trocknen lassen.</p>
                </div>
              </div>

              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">🕯️ Duftkerze gießen</h4>
                <p className="text-sm text-[#4B5563]">Mit Sojawachs, Docht und ätherischen Ölen (Oud, Amber, Rose) eine eigene Kerze kreieren.</p>
              </div>

              <div className="bg-[#F5F5F0] rounded-xl p-6">
                <h4 className="font-medium text-[#2C332E] mb-3">💆 Spa-Set zusammenstellen</h4>
                <p className="text-sm text-[#4B5563]">Selbstgemachte Produkte (Körperöl, Badebomben, Lippenbalsam) in einer schönen Box verpacken.</p>
              </div>
            </div>
          </section>

          <section id="kinder" className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                <Gift className="w-6 h-6 text-[#4A5D4F]" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">4. DIY mit Kindern</h2>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Handabdruck-Karte</h4>
                <p className="text-sm text-[#4B5563]">Kinderhand mit Farbe auf Karte drücken und zu Mond/Stern gestalten.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Eid-Coupon-Buch</h4>
                <p className="text-sm text-[#4B5563]">"Gutschein für: 1x Umarmung, 1x Zimmer aufräumen" – süße Geschenke von Kindern für Eltern.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Verzierte Gläser</h4>
                <p className="text-sm text-[#4B5563]">Marmeladengläser mit Farbe, Glitzer und Bändern dekorieren – als Teelichthalter oder Aufbewahrung.</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Fazit</h2>
            <p className="text-[#4B5563] leading-relaxed mb-4">
              DIY-Geschenke zeigen, dass du dir Zeit genommen hast. Sie müssen nicht perfekt sein – 
              die Liebe und Mühe, die du hineinsteckst, macht sie wertvoll. Ob Dattel-Trüffel, 
              handgemachte Karte oder duftende Kerze: Ein selbstgemachtes Geschenk kommt von Herzen.
            </p>
            <p className="text-xl text-[#4A5D4F] font-medium">Eid Mubarak! 🌙</p>
          </section>

          <div className="bg-[#F5F5F0] rounded-2xl p-6 mb-8">
            <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-4">Weitere Artikel</h3>
            <ul className="space-y-2">
              <li><Link to="/ratgeber/guenstige-eid-geschenke" className="text-[#4A5D4F] hover:underline">→ Günstige Eid Geschenke unter 20€</Link></li>
              <li><Link to="/ratgeber/eid-deko-ideen" className="text-[#4A5D4F] hover:underline">→ Eid Deko Ideen für Zuhause</Link></li>
            </ul>
          </div>

          <div className="bg-[#4A5D4F] rounded-2xl p-8 text-center">
            <h3 className="font-serif text-2xl font-medium text-white mb-4">Mehr Inspiration</h3>
            <Link to="/geschenke" className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-8 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors">
              Geschenke entdecken<Gift className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </article>
    </main>
  );
}
